import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import useSelector from 'common/hooks/useSelector';
import { AccountCard } from 'features/home/customerHome/components/cards';
import {
  DepositCard,
  WithdrawCard,
} from 'features/banking/customerBanking/components/cards';
import { defaultCardState } from 'features/home/customerHome/localState/states';
import { cardRecipe } from 'features/home/customerHome/localState/recipes';
import FadeIn from 'react-fade-in';
import { TransfersTable } from './components/transfersTable';
import { doGetExternalBankAccounts } from 'common/state/banking/thunkActions';
import { doGetUserAccountsWithBalance } from 'common/state/accounts/thunkActions';
import {
  CustomerSummariesActions,
  CustomerSummary,
} from 'common/state/customers/actions';
import { customerSummariesRecipe } from 'common/state/customers/recipes';
import {
  CustomerSummariesState,
  defaultCustomerSummariesState,
} from 'common/state/customers/states';
import { doGetAllCustomerSummaries } from 'common/state/customers/thunkActions';
import { Currency } from 'common/classes/currency';
import { BaseTable, ColumnProp, Loading } from 'common/components/table/base';
import { UsernameLink } from '../agentHome/agentHome';
import { mdiPiggyBank } from '@mdi/js';
import { TableTitle } from 'common/components/tables';

const PageContainer = styled.div`
  margin: auto;
  min-height: 100%;
`;

const TransferCardContainer = styled.div`
  margin: 1rem;
`;

const TableContainer = styled.div`
  margin: 1rem;
`;

export type TransactionMode = null | 'deposit' | 'withdraw';

function CustomerHome() {
  const [state, dispatch] = useProducerWithThunks(cardRecipe, defaultCardState);
  const [transactionMode, setTransactionMode] =
    React.useState<TransactionMode>(null);
  const [customerState, customerDispatch] = useProducerWithThunks<
    CustomerSummariesState,
    CustomerSummariesActions
  >(customerSummariesRecipe, defaultCustomerSummariesState);

  useEffect(() => {
    customerDispatch(doGetAllCustomerSummaries());
  }, [customerDispatch]);

  React.useEffect(() => {
    dispatch(doGetUserAccountsWithBalance());
    dispatch(doGetExternalBankAccounts());
  }, [dispatch]);

  const { clients } = useSelector((state) => state.clients);
  const { wallets } = useSelector((state) => state.wallets);
  const { accounts, bankAccounts } = state;

  const customerColumns = useMemo(
    (): ColumnProp<CustomerSummary>[] => [
      {
        key: 'customer_username',
        title: 'Customer',
        valueFormatter: (row: CustomerSummary) => (
          <UsernameLink to={`/customers/${row.customer_username}`}>
            {row.customer_username}
          </UsernameLink>
        ),
        sortValueGenerator: (row: CustomerSummary) =>
          row.customer_username.toLowerCase(),
        width: 112,
      },
      { key: 'customer_country', title: 'Country', width: 132 },
      { key: 'wallet_currency', title: 'Currency', width: 96 },
      {
        key: 'upline_settled',
        title: 'PL Settled',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.cleared, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.cleared, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared),
        width: 128,
      },
      {
        key: 'reconcilled',
        title: 'Reconcilled Under Settlement Threshold',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            row.balances.reconciled,
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.reconciled, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.reconciled),
        width: 184,
      },
      {
        key: 'pending',
        title: 'Pending To Reconcile Prior Weeks',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.pending, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.pending, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.pending),
        width: 184,
      },
      {
        key: 'outstanding',
        title: 'Current Week Outstanding (Cached)',
        valueFormatter: (row: CustomerSummary) => (
          row.balances.outstanding !== null
            ? <div title={`Cached at ${row.balances.outstanding_age}`}>
                {new Currency(
                  row.balances.outstanding,
                  row.wallet_currency
                ).toElement()}
              </div>
            : <Loading small>Loading...</Loading>
        ),
        csvFormatter: (row) =>
          new Currency(row.balances.outstanding, row.wallet_currency).toString({
            symbol: false,
          }),
        width: 128,
      },
      {
        key: 'total',
        title: 'Total PL',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding),
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding),
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared) +
          parseFloat(row.balances.reconciled) +
          parseFloat(row.balances.pending) +
          parseFloat(row.balances.outstanding),
        width: 128,
        className: 'highlight',
      },
      {
        key: 'deposits',
        title: 'Deposits / Withdrawals',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.balances.deposits, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.deposits, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.deposits),
        width: 128,
      },
      {
        key: 'ajustments',
        title: 'Adjustments',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            row.balances.adjustments,
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(row.balances.adjustments, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.adjustments),
        width: 128,
      },
      {
        key: 'final_balance',
        title: 'Client Account Balance',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.deposits) +
              parseFloat(row.balances.adjustments),
            row.wallet_currency
          ).toElement(),
        csvFormatter: (row) =>
          new Currency(
            parseFloat(row.balances.cleared) +
              parseFloat(row.balances.reconciled) +
              parseFloat(row.balances.pending) +
              parseFloat(row.balances.outstanding) +
              parseFloat(row.balances.deposits) +
              parseFloat(row.balances.adjustments),
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.balances.cleared) +
          parseFloat(row.balances.reconciled) +
          parseFloat(row.balances.pending) +
          parseFloat(row.balances.outstanding) +
          parseFloat(row.balances.deposits) +
          parseFloat(row.balances.adjustments),
        width: 160,
        className: 'highlight',
      },
      {
        key: 'wallet_min_balance',
        title: 'Min. Balance',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.wallet_min_balance, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.wallet_min_balance, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.wallet_min_balance),
        width: 128,
      },
      {
        key: 'wallet_credit',
        title: 'Credit Limit',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.wallet_credit, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.wallet_credit, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.wallet_credit),
        width: 128,
      },
      {
        key: 'wallet_threshold',
        title: 'Profit / Losses - Settlement Threshold',
        valueFormatter: (row: CustomerSummary) =>
          new Currency(row.wallet_threshold, row.wallet_currency).toElement(),
        csvFormatter: (row) =>
          new Currency(row.wallet_threshold, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row: CustomerSummary) =>
          parseFloat(row.wallet_threshold),
        width: 184,
      },
      {
        key: 'last_placement_date',
        title: 'Last Placement Date',
        valueFormatter: (row: CustomerSummary) =>
          new Date(row.last_placement_date).toLocaleDateString(),
        width: 160,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <TransferCardContainer>
        <AccountCard
          accounts={accounts}
          setTransactionMode={setTransactionMode}
          transactionMode={transactionMode}
          clients={clients}
          wallets={wallets}
        />
        {transactionMode === 'deposit' ? (
          <DepositCard
            setTransactionMode={setTransactionMode}
            wallets={wallets}
            bankAccounts={bankAccounts}
          />
        ) : transactionMode === 'withdraw' ? (
          <WithdrawCard
            setTransactionMode={setTransactionMode}
            wallets={wallets}
            bankAccounts={bankAccounts}
          />
        ) : null}
      </TransferCardContainer>
      <FadeIn>
        <TableTitle icon={mdiPiggyBank} title="Customer Summary" />
        <BaseTable
          columns={customerColumns}
          rows={customerState.summaries}
          csvName="Customers"
          defaultSortColumn="customer_username"
          isLoading={customerState.pending}
          error={customerState.error}
        />
      </FadeIn>
      <FadeIn>
        <TableContainer>
          <TransfersTable />
        </TableContainer>
      </FadeIn>
    </PageContainer>
  );
}

export default CustomerHome;
